import {
  ApartmentOutlined,
  ClusterOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  TrophyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Popover, Space, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteTopPlanApi from "../api/DeleteTopPlanApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../helpers/NotificationHelpers";
import { permissionAbility } from "../helpers/permissionHelpers";
import { getAmountInText, getNumberInText } from "../helpers/targetHelpers";
import CascadePlanModal from "./modal/CascadePlanModal";
import CascadePlanHierarchyModal from "./modal/CascadePlanHierarchyModal";
import EditTopPlanModal from "./modal/edit/EditTopPlanModal";
import HardDeleteModal from "./modal/HardDeleteModal";
import TopPlansSpace from "./TopPlansSpace";
import { useEffect } from "react";
import UpdateTargetProgressModal from "./modal/UpdateTargetProgressModal";
import UpdateTargetApi from "../api/target-progress/UpdateTargetApi";

function TopPlanElement(props) {
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [hierarchyModalVisible, setHierarchyModalVisible] = useState(false);
  const [editPlan, setEditPlan] = useState();
  const [cascadePlan, setCascadePlan] = useState();
  const [deletePlanId, setDeletePlanId] = useState("");
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const isCycleCompleted = perfCycleState?.status === "Completed";
  const userLevel = positionState?.userRole?.name;
  const [activeKeys, setActiveKeys] = useState([]);
  const [cascade, setCascade] = useState(false);
  const [collapse, setCollapse] = useState(true);
  const [canCollapse, setCanCollapse] = useState(true);
  const [cascadedData, setCascadedData] = useState();
  const [cascadeCount, setCascadeCount] = useState(0);
  const [update, setUpdate] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [targetProgress, setTargetProgress] = useState(0);
  const [hasPendingTargetProgress, setHasPendingTargetProgress] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // 🔑 NEW: Track if this is a newly added plan for visual feedback
  const [isNewlyAdded, setIsNewlyAdded] = useState(false);

  useEffect(() => {
    // Check if this plan was just created (within last 5 seconds)
    if (props.data?.createdOn) {
      const createdTime = new Date(props.data.createdOn).getTime();
      const now = new Date().getTime();
      const fiveSecondsAgo = now - 5000;
      
      if (createdTime > fiveSecondsAgo) {
        setIsNewlyAdded(true);
        // Remove highlight after 3 seconds
        setTimeout(() => setIsNewlyAdded(false), 3000);
      }
    }
  }, [props.data?.createdOn]);

  useEffect(() => {
    setTargetProgress(props.data?.achievedTarget || 0);
    setHasPendingTargetProgress(props.data?.hasPendingTargetProgress || false);
  }, [props.data]);

  useEffect(() => {
    if (props.data?.canUpdate) {
      setCanCollapse(false);
    } else {
      setCanCollapse(true);
    }
  }, [props.data]);

  function getTargetString(target, scale, abb) {
    let targetString = "";
    if (scale === "Number") {
      targetString = getNumberInText(target);
    } else if (scale === "Currency") {
      targetString = getAmountInText(target, abb);
    } else if (scale === "Percentage") {
      targetString = target + "%";
    } else {
      targetString = target + abb;
    }
    return targetString;
  }

  async function deletePlan() {
    const apiResponse = await DeleteTopPlanApi(deletePlanId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Top Plan deleted");

      props.handleDelete(deletePlanId);
      props.handleDeleteChange(props.data?.planId, props.totalChildCount - 1);
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeletePlanId("");
  }

  function handleExpand(element) {
    setActiveKeys([props.data?.planId]);
  }

  function handleCascade(cascadedPlan) {
    // 🔑 NEW: Add cascaded plan at the TOP of the children list for immediate visibility
    setCascadedData(cascadedPlan);
    setCascadeCount(cascadeCount + 1);
    
    // Ensure the parent is expanded to show the new cascade
    if (collapse) {
      setCollapse(false);
    }
  }

  function handleDeleteChange(planId, totalChildCount) {
    totalChildCount === 0 && setCanCollapse(false);
  }

  const getBaseUrl = () => {
    if (location.pathname.includes("top-plans")) {
      return "/top-plans";
    } else if (location.pathname.includes("my-plans")) {
      return "/my-plans";
    } else {
      return "/";
    }
  };
  const handleNavigate = (planId) => {
    const baseUrl = getBaseUrl();
    navigate(`${baseUrl}/${planId}`);
  };

  async function onFinish(values) {
    setSubmit(true);
    const apiResponse = await UpdateTargetApi(
      props.data?.planId,
      values.target,
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      if (apiResponse.targetProgress?.approvalStatus === "Approved") {
        setTargetProgress(values.target);
        setHasPendingTargetProgress(false);
        openNotificationSuccess("bottomRight", `Target updated`);
      } else {
        // Immediately set pending status to true for instant visual feedback
        setHasPendingTargetProgress(true);
        openNotificationSuccess(
          "bottomRight",
          `Target updated but approval is pending`
        );
      }
      // Update the parent list with the new data
      if (props.handleEdit) {
        const performance = props.data?.target && props.data.target !== 0
          ? parseFloat(((values.target / props.data.target) * 100).toFixed(2))
          : 0;
        
        const updatedPlan = {
          ...props.data,
          achievedTarget: values.target,
          hasPendingTargetProgress: apiResponse.targetProgress?.approvalStatus !== "Approved",
          performance: performance
        };
        props.handleEdit(updatedPlan);
      }
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update target. Try again later."
      );
    }
    setSubmit(false);
    setUpdate(false);
  }

  function getPerformance() {
    // Use the performance from data if available, otherwise calculate
    if (props.data?.performance !== undefined && props.data?.performance !== null) {
      const perf = parseFloat(props.data.performance);
      if (isNaN(perf)) {
        return "0.00";
      }
      return perf.toFixed(2);
    }
    
    let output = 0;
    if (props.data?.target && props.data.target !== 0) {
      output = ((targetProgress / props.data.target) * 100);
    }

    if (isNaN(output) || !isFinite(output)) {
      return "0.00";
    }
    return output.toFixed(2);
  }

  return (
    <>
      <>
        <div
          className={`flex items-center rounded-md cursor-pointer transition-all duration-300 ${
            isNewlyAdded ? 'bg-green-50 border-2 border-green-300' : ''
          }`}
          onClick={() => handleNavigate(props.data?.planId)}
        >
          <div className="flex justify-center items-center w-8">
            {canCollapse ? (
              collapse ? (
                <PlusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleExpand(props.data?.planId);
                    setCollapse(!collapse);
                  }}
                />
              ) : (
                <MinusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCollapse(!collapse);
                  }}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className={"grid grid-cols-5 ml-2 p-3 border gap-5 w-full"}>
            <div className="col-span-3">
              <div className="mb-1">
                {props.data?.title}{" "}
                <Tag>{props.data?.planGroup.toUpperCase()}</Tag>
              </div>
              <div className="flex items-center gap-1">
                <div>
                  <Tag
                    color={
                      props.data?.progressStatus === "On Track"
                        ? "Green"
                        : props.data?.progressStatus === "Off Track"
                        ? "Orange"
                        : "Red"
                    }
                  >
                    {(getPerformance() || "0") + "%"}
                  </Tag>
                </div>
                <div>
                  {getTargetString(
                    props.data?.target,
                    props.data?.scale?.scaleName,
                    props.data?.scale?.scaleUnitAbbreviation
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-2 flex justify-between items-start">
              <div>
                <div className="flex items-center">
                  {props.data?.assignedTo?.imageUrl ? (
                    <Avatar
                      style={{ minWidth: "32px" }}
                      className="cursor-pointer"
                      src={props.data?.assignedTo?.imageUrl}
                    />
                  ) : (
                    <Avatar style={{ minWidth: "32px" }}>
                      {props.data?.assignedTo?.firstName ? (
                        props.data?.assignedTo?.firstName
                          ?.charAt(0)
                          .toUpperCase()
                      ) : (
                        <UserOutlined />
                      )}
                    </Avatar>
                  )}
                  <div className="ml-2">
                    <div>{props.data?.assignedTo?.firstName || "-"}</div>
                    <div className="flex items-center gap-1 -mt-1">
                      <div className="text-xs ">
                        {props.data?.assignedTo?.designationAbb},
                      </div>
                      <div className="flex items-center">
                        <div
                          className="min-w-[8px] h-2 mr-1 rounded-full"
                          style={{
                            backgroundColor: props.data?.unit?.orgLevelColor,
                          }}
                        />
                        <div className="truncate">{props.data?.unit?.name}</div>
                        <Popover
                          placement="top"
                          content={
                            <div className="text-xs">
                              {props.data?.unit?.name}
                              {props.data?.unit?.headUnit &&
                                " < " + props.data?.unit?.headUnit?.name}
                              {props.data?.unit?.headUnit?.headUnit &&
                                " < " +
                                  props.data?.unit?.headUnit?.headUnit?.name}
                            </div>
                          }
                          trigger="hover"
                        >
                          <div className="text-gray-500 text-xs ml-1">
                            <InfoCircleOutlined />
                          </div>
                        </Popover>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Space>
                {props.data?.canUpdate &&
                  positionState?.id === props.data?.assignedTo?.positionId && (
                    isCycleCompleted ? (
                      <Tooltip title={"Performance cycle is completed"}>
                        <div className="text-gray-300 cursor-not-allowed">
                          <TrophyOutlined />
                        </div>
                      </Tooltip>
                    ) : hasPendingTargetProgress ? (
                      <Tooltip title={"Target update pending approval"}>
                        <div className="text-red-500 cursor-not-allowed">
                          <TrophyOutlined />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip title={"Update Target"}>
                        <div
                          onClick={(event) => {
                            event.stopPropagation();
                            setUpdate(true);
                          }}
                          className="text-blue-500 hover:text-blue-600 cursor-pointer"
                        >
                          <TrophyOutlined />
                        </div>
                      </Tooltip>
                    )
                  )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={"Cascade"}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        setCascade(true);
                        setCascadePlan(props.data);
                      }}
                      className="text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      <ClusterOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={"Manage Cascade Hierarchy"}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        setHierarchyModalVisible(true);
                      }}
                      className="text-green-500 hover:text-green-600 cursor-pointer"
                    >
                      <ApartmentOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  isCycleCompleted ? (
                    <Tooltip title={"Performance cycle is completed"}>
                      <div className="text-gray-300 cursor-not-allowed">
                        <EditOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip title={"Edit"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditPlan(props.data);
                          setEditModal(true);
                        }}
                        className="text-blue-500 hover:text-blue-600 cursor-pointer"
                      >
                        <EditOutlined />
                      </div>
                    </Tooltip>
                  )
                )}
                {permissionAbility(userLevel, "plans", "delete") &&
                  (!canCollapse ? (
                    <Tooltip title={"Delete"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeletePlanId(props.data?.planId);
                          setDeleteModal(true);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <DeleteOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Popover
                      placement="left"
                      content={
                        <div className="text-xs w-48">
                          Can't delete this element. This plan was cascaded.
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500">
                        <DeleteOutlined />
                      </div>
                    </Popover>
                  ))}
              </Space>
            </div>
          </div>
        </div>
        <div className={"ml-5 " + (collapse ? "hidden" : "block")}>
          <TopPlansSpace
            plans={props.plans}
            planId={props.data?.planId}
            activeKeys={activeKeys}
            cascadedData={cascadedData}
            collapse={collapse}
            handleDeleteChange={(planId, totalChildCount) =>
              handleDeleteChange(planId, totalChildCount)
            }
            cascadeCount={cascadeCount}
          />
        </div>
      </>

      <EditTopPlanModal
        visible={editModal}
        handleCancel={() => {
          setEditPlan();
          setEditModal(false);
        }}
        editPlan={editPlan}
        handleOk={(data) => props.handleEdit(data)}
      />
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => {
          deletePlan();
        }}
        text={`Are you sure want to delete this ${topPlanState}?`}
        title={`Delete ${topPlanState}`}
      />
      <CascadePlanModal
        visible={cascade}
        handleCancel={() => {
          setCascadePlan();
          setCascade(false);
        }}
        plan={cascadePlan}
        handleOk={(cascadedPlan) => {
          setCanCollapse(true);
          handleCascade(cascadedPlan);
          setCollapse(false);
        }}
      />

      <CascadePlanHierarchyModal
        visible={hierarchyModalVisible}
        onCancel={() => setHierarchyModalVisible(false)}
        planId={props.data?.planId}
        planTitle={props.data?.title}
        onHierarchyChange={() => {
          // Refresh the parent component data
          if (props.onRefresh) {
            props.onRefresh();
          }
        }}
      />

      {update && (
        <UpdateTargetProgressModal
          visible={update}
          handleCancel={() => setUpdate(false)}
          data={props.data}
          targetProgress={targetProgress}
          onFinish={onFinish}
          submit={submit}
        />
      )}
    </>
  );
}

export default TopPlanElement;
